import React, { useEffect, useState } from 'react';
import './Voicemail.css';

export default function Voicemail() {
  const [loading, setLoading] = useState(true);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [recordings, setRecordings] = useState([]);
  const [selected, setSelected] = useState({});
  const [toast, setToast] = useState(null);

  useEffect(() => {
    fetchVoicemail();
  }, []);

  async function fetchVoicemail() {
    try {
      const res = await fetch('/api/voicemail');
      const data = await res.json();
      if (data.success) {
        const withIds = data.recordings.reverse().map((rec, idx) => ({
          ...rec,
          uniqueId: `${rec.phoneNumber}_${rec.timestamp || Date.now()}_${idx}`,
        }));
        setRecordings(withIds);
      }
    } catch (err) {
      console.error('Error fetching voicemail:', err);
      showToast('Error fetching voicemail', true);
    } finally {
      setLoading(false);
    }
  }

  function showToast(message, isError = false) {
    setToast({ message, isError });
    setTimeout(() => setToast(null), 3500);
  }

  function toggle(key) {
    setSelected((prev) => ({ ...prev, [key]: !prev[key] }));
  }

  function selectAll() {
    if (Object.keys(selected).length === recordings.length) {
      setSelected({});
    } else {
      const all = {};
      recordings.forEach((rec, i) => { all[`${rec.phoneNumber}_${i}`] = true; });
      setSelected(all);
    }
  }

  function getSelected() {
    return recordings.filter((rec, i) => selected[`${rec.phoneNumber}_${i}`]);
  }

  async function uploadToGithub() {
    const chosen = getSelected();
    if (!chosen.length) return showToast('Please select at least one recording', true);
    if (!window.confirm(`Upload ${chosen.length} recording(s) to GitHub?`)) return;

    setUploadLoading(true);
    try {
      const res = await fetch('/api/voicemail/upload-to-github', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ selectedRecordings: chosen }),
      });
      const data = await res.json();
      if (data.success) {
        showToast(data.message);
        setSelected({});
      } else {
        showToast(data.message || 'Upload failed', true);
      }
    } catch (err) {
      console.error('Error uploading to GitHub:', err);
      showToast('Error uploading recordings', true);
    } finally {
      setUploadLoading(false);
    }
  }

  async function saveLocal() {
    const chosen = getSelected();
    if (!chosen.length) return showToast('Please select at least one recording', true);
    setUploadLoading(true);
    try {
      const res = await fetch('/api/voicemail/save-local', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ selectedRecordings: chosen }),
      });
      const data = await res.json();
      if (data.success) {
        showToast(data.message);
        setSelected({});
      } else {
        showToast(data.message || 'Save failed', true);
      }
    } catch (err) {
      console.error('Error saving locally:', err);
      showToast('Error saving recordings', true);
    } finally {
      setUploadLoading(false);
    }
  }

  function deleteSelected() {
    const chosen = getSelected();
    if (!chosen.length) return showToast('Please select at least one recording', true);
    if (!window.confirm(`Delete ${chosen.length} recording(s)?`)) return;
    // NOTE: ported as-is from the source file — delete was never actually
    // implemented there either ("Delete functionality to be implemented").
    showToast('Delete functionality to be implemented');
  }

  const selectedCount = Object.values(selected).filter(Boolean).length;

  if (loading) {
    return (
      <div className="vm-loading"><div className="vm-spinner" /></div>
    );
  }

  return (
    <div className="vm-view">
      <div className="vm-header">
        <h1>New Messages ({recordings.length})</h1>
        <button className="vm-select-all" onClick={selectAll}>
          {Object.keys(selected).length === recordings.length ? 'Deselect all' : 'Select all'}
        </button>
      </div>

      {selectedCount > 0 && (
        <div className="vm-action-bar">
          <span>{selectedCount} recording(s) selected</span>
          <div className="vm-action-buttons">
            <button className="mihu-btn-primary-lg" onClick={uploadToGithub} disabled={uploadLoading}>
              {uploadLoading ? 'Working…' : 'Upload to GitHub'}
            </button>
            <button className="vm-btn-secondary" onClick={saveLocal} disabled={uploadLoading}>Save local</button>
            <button className="vm-btn-danger" onClick={deleteSelected} disabled={uploadLoading}>Delete</button>
          </div>
        </div>
      )}

      {recordings.length === 0 ? (
        <div className="vm-empty">
          <div className="vm-empty-icon">📭</div>
          <p>No voicemail messages</p>
        </div>
      ) : (
        <div className="vm-list">
          {recordings.map((rec, i) => {
            const key = `${rec.phoneNumber}_${i}`;
            const isSelected = selected[key];
            return (
              <div key={rec.uniqueId} className={"vm-row" + (isSelected ? " selected" : "")}>
                <input type="checkbox" checked={!!isSelected} onChange={() => toggle(key)} />
                <div className="vm-row-body">
                  <audio controls>
                    <source src={rec.url} type="audio/wav" />
                  </audio>
                  <div className="vm-row-phone">{rec.phoneNumber}</div>
                  <div className="vm-row-transcript">{rec.transcription || 'No transcription available'}</div>
                  <div className="vm-row-time">{rec.timestamp ? new Date(rec.timestamp).toLocaleString() : ''}</div>
                </div>
                <span className="vm-row-badge">{recordings.length - i}</span>
              </div>
            );
          })}
        </div>
      )}

      {toast && (
        <div className={"vm-toast" + (toast.isError ? " error" : "")}>{toast.message}</div>
      )}
    </div>
  );
}
